import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { managementCreateButtonSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx } from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import {
    DEFAULT_PACE_MS,
    cancelRun,
    formatDuration,
    getPreview,
    getRuns,
    isActive,
    pauseRun,
    progressPercent,
    resumeRun,
    startRun,
    validatePace,
} from './rescore.service';

const POLL_MS = 10000;

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #DFE5F6',
    backgroundColor: '#FFFFFF',
};

const captionSx = {
    fontSize: 12,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: '#625B71',
    fontWeight: 600,
};

const STATUS_CHIP = {
    RUNNING: { label: 'Running', bg: '#EEF7E6', color: '#4F8506' },
    PAUSED: { label: 'Paused', bg: '#FFF5E1', color: '#8A5A00' },
    COMPLETED: { label: 'Completed', bg: '#E8EEFB', color: '#294190' },
    CANCELLED: { label: 'Cancelled', bg: '#F1F1F1', color: '#5B6472' },
};

function StatusChip({ status }) {
    const chip = STATUS_CHIP[status] || { label: status, bg: '#F1F1F1', color: '#5B6472' };
    return <Chip size="small" label={chip.label} sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: 600 }} />;
}

function Count({ caption, value, color = '#1C1B1F' }) {
    return (
        <Box>
            <Typography sx={captionSx}>{caption}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color }}>{value}</Typography>
        </Box>
    );
}

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : '—');

/**
 * Bulk re-scoring of existing farmers.
 *
 * A run goes through every farmer who has answered the questionnaire, one at a time and by default only
 * off-peak, and gives each a fresh score from their own last answers. Shown here: what a run would do
 * before it starts, how far the current one has got, and the controls to pause, resume or cancel it.
 */
function Rescore() {
    const [preview, setPreview] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [paceSeconds, setPaceSeconds] = useState(String(DEFAULT_PACE_MS / 1000));
    const [offPeakOnly, setOffPeakOnly] = useState(true);
    const [confirmStart, setConfirmStart] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const paceIssue = validatePace(paceSeconds);
    const paceMs = paceIssue ? null : Math.round(Number(paceSeconds) * 1000);
    const active = runs.find(isActive) || null;
    const history = runs.filter((run) => !isActive(run));

    const loadRuns = useCallback(async () => {
        const result = await getRuns();
        if (!result.success) {
            setLoadError(result.error.message);
            return;
        }
        setLoadError('');
        setRuns(result.data);
    }, []);

    useEffect(() => {
        loadRuns();
    }, [loadRuns]);

    useEffect(() => {
        if (!paceMs) return;
        let current = true;
        getPreview(paceMs).then((result) => {
            if (current && result.success) setPreview(result.data);
        });
        return () => {
            current = false;
        };
    }, [paceMs, runs.length]);

    // Keep the progress live while a run is in progress.
    useEffect(() => {
        if (!active) return undefined;
        const timer = setInterval(loadRuns, POLL_MS);
        return () => clearInterval(timer);
    }, [active, loadRuns]);

    const act = async (action, success) => {
        setBusy(true);
        const result = await action();
        setBusy(false);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        notify(success);
        loadRuns();
    };

    const handleStart = async () => {
        setConfirmStart(false);
        await act(() => startRun({ paceMs, offPeakOnly }), 'Re-score run started.');
    };

    const handleCancel = async () => {
        setConfirmCancel(false);
        await act(() => cancelRun(active.id), 'Re-score run cancelled.');
    };

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        Give every farmer who has answered the questionnaire a fresh eK Score from their own last answers.
                    </Typography>
                </Box>
            </Box>

            <Alert severity="warning" sx={{ borderRadius: '16px', mb: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Scores will change for the farmers a run reaches</AlertTitle>
                Re-scoring reads today's data: the farmer's current land and crop, live weather and market prices.
                Risk scores and their confidence can shift, and SRFS and the banks see the new values. Tell them before
                starting a run.
            </Alert>

            {loadError && <Alert severity="error" sx={{ borderRadius: '16px', mb: 3 }}>{loadError}</Alert>}

            {active ? (
                <Paper elevation={0} sx={{ ...cardSx, mb: 3 }} data-testid="rescore-active-run">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F' }}>Current run</Typography>
                        <StatusChip status={active.status} />
                        <Typography sx={{ fontSize: 14, color: '#625B71' }}>
                            Started {formatWhen(active.createdAt)} · one farmer every {active.paceMs / 1000} s
                            {active.offPeakOnly ? ' · off-peak only' : ''}
                        </Typography>
                    </Box>

                    <LinearProgress
                        variant="determinate"
                        value={progressPercent(active)}
                        sx={{ height: 10, borderRadius: 5, mb: 1, backgroundColor: '#EEF2F7', '& .MuiLinearProgress-bar': { backgroundColor: '#4F8506' } }}
                    />
                    <Typography sx={{ fontSize: 13, color: '#625B71', mb: 2 }}>
                        {progressPercent(active)}% of {active.total} farmers
                        {active.offPeakOnly && preview && !preview.offPeakNow && active.status === 'RUNNING'
                            ? ` · waiting for the off-peak window (${preview.offPeakWindow})`
                            : ''}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                        <Count caption="Re-scored" value={active.done} color="#4F8506" />
                        <Count caption="Skipped" value={active.skipped} />
                        <Count caption="Failed" value={active.failed} color={active.failed ? '#DA342E' : '#1C1B1F'} />
                        <Count caption="Total" value={active.total} />
                    </Box>

                    {active.lastError && (
                        <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>
                            Last failure: {active.lastError}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {active.status === 'RUNNING' ? (
                            <Button
                                disabled={busy}
                                onClick={() => act(() => pauseRun(active.id), 'Re-score run paused.')}
                                startIcon={<Icon icon="tabler:player-pause" width="18" height="18" />}
                                sx={{ textTransform: 'none', color: '#294190', fontWeight: 600 }}
                            >
                                Pause
                            </Button>
                        ) : (
                            <Button
                                disabled={busy}
                                onClick={() => act(() => resumeRun(active.id), 'Re-score run resumed.')}
                                startIcon={<Icon icon="tabler:player-play" width="18" height="18" />}
                                sx={{ textTransform: 'none', color: '#4F8506', fontWeight: 600 }}
                            >
                                Resume
                            </Button>
                        )}
                        <Button
                            disabled={busy}
                            onClick={() => setConfirmCancel(true)}
                            startIcon={<Icon icon="tabler:x" width="18" height="18" />}
                            sx={{ textTransform: 'none', color: '#DA342E', fontWeight: 600 }}
                        >
                            Cancel run
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Paper elevation={0} sx={{ ...cardSx, mb: 3 }} data-testid="rescore-new-run">
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mb: 2 }}>New run</Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                        <Box>
                            <FieldLabel label="Seconds between farmers" required />
                            <TextField
                                fullWidth
                                type="number"
                                value={paceSeconds}
                                onChange={(event) => setPaceSeconds(event.target.value)}
                                error={Boolean(paceIssue)}
                                helperText={paceIssue || 'Each re-score calls NASA POWER and data.gov.in; keep this slow.'}
                                inputProps={{ min: 0.5, max: 60, step: 0.5 }}
                                sx={formFieldSx}
                            />
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={<Switch checked={offPeakOnly} onChange={(event) => setOffPeakOnly(event.target.checked)} color="success" />}
                                label="Off-peak only"
                                sx={{ mt: 3 }}
                            />
                            <Typography sx={{ fontSize: 13, color: '#625B71' }}>
                                {preview ? `Runs only between ${preview.offPeakWindow}, when farmers and agents are not using the apps.` : ''}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ backgroundColor: '#F8FAFF', border: '1px solid #DFE5F6', borderRadius: '12px', p: 2, mb: 3 }}>
                        {preview ? (
                            <Typography sx={{ fontSize: 15, color: '#1C1B1F' }}>
                                <b>{preview.farmers.toLocaleString()}</b> farmer{preview.farmers === 1 ? '' : 's'} would be re-scored,
                                taking {formatDuration(preview.estimatedMinutes)} of running time
                                {offPeakOnly ? ', spread over off-peak hours' : ''}.
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: 15, color: '#625B71' }}>Working out how many farmers…</Typography>
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        disabled={busy || Boolean(paceIssue) || !preview || preview.farmers === 0}
                        onClick={() => setConfirmStart(true)}
                        sx={{ ...managementCreateButtonSx, minWidth: 220 }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:refresh" width="18" height="18" />
                            <span>START RE-SCORING</span>
                        </Box>
                    </Button>
                </Paper>
            )}

            {history.length > 0 && (
                <Paper elevation={0} sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
                    <Typography sx={{ ...captionSx, px: 3, pt: 2.5, pb: 1 }}>Earlier runs</Typography>
                    {history.map((run, index) => (
                        <Box
                            key={run.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                px: 3,
                                py: 1.5,
                                flexWrap: 'wrap',
                                borderTop: index === 0 ? '1px solid #EEF2F7' : 'none',
                                borderBottom: index === history.length - 1 ? 'none' : '1px solid #EEF2F7',
                            }}
                        >
                            <StatusChip status={run.status} />
                            <Typography sx={{ fontSize: 14, color: '#1C1B1F', minWidth: 180 }}>{formatWhen(run.createdAt)}</Typography>
                            <Typography sx={{ fontSize: 14, color: '#625B71' }}>
                                {run.done} re-scored · {run.skipped} skipped · {run.failed} failed · of {run.total}
                            </Typography>
                        </Box>
                    ))}
                </Paper>
            )}

            <Dialog open={confirmStart} onClose={() => setConfirmStart(false)} PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Start re-scoring?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#5B6472' }}>
                        {preview?.farmers.toLocaleString()} farmers will get a new eK Score, one every {paceSeconds} s
                        {offPeakOnly ? `, only between ${preview?.offPeakWindow}` : ', starting now'}. SRFS and the banks will
                        see the new scores. You can pause or cancel at any time.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                    <Button onClick={() => setConfirmStart(false)} sx={createEditCancelButtonSx}>Not now</Button>
                    <Button variant="contained" onClick={handleStart} sx={createEditPrimaryButtonSx}>Start</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Cancel this run?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#5B6472' }}>
                        Farmers already re-scored keep their new score. The rest keep their old one. A cancelled run cannot be resumed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                    <Button onClick={() => setConfirmCancel(false)} sx={createEditCancelButtonSx}>Keep running</Button>
                    <Button
                        variant="contained"
                        onClick={handleCancel}
                        sx={{ ...createEditPrimaryButtonSx, bgcolor: '#DA342E', '&:hover': { bgcolor: '#B42318' } }}
                    >
                        Cancel run
                    </Button>
                </DialogActions>
            </Dialog>

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default Rescore;
